import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { Control, FieldValues, FieldPath } from "react-hook-form";
import { ComponentType, ReactNode } from "react";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { I18nNamespace } from "@/i18n/generated/keys.generated";

type CustomInputProps<T extends FieldValues> = {
  control: Control<T>;
  name: FieldPath<T>;
  label?: ReactNode;
  classNameLabel?: string;
  type?: string;
  placeholder?: string;
  className?: string;
  rightIcon?: ComponentType<{ size?: number }>;
  onIconClick?: () => void;
  readOnly?: boolean;
  disabled?: boolean;
  showClear?: boolean;
  unshowMessage?: boolean;
  keyT: I18nNamespace;
} & React.InputHTMLAttributes<HTMLInputElement>;

export function InputForm<T extends FieldValues>({
  rightIcon,
  onIconClick,
  control,
  name,
  label,
  classNameLabel,
  type = "text",
  placeholder = "",
  className = "",
  readOnly = false,
  disabled = false,
  showClear = true,
  unshowMessage = false,
  keyT,
}: CustomInputProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const RightIcon = rightIcon;
        const inputId = `input-${name.replace(/\./g, "-")}`;

        const shouldShowClear =
          showClear &&
          !!field.value &&
          type !== "number" &&
          !readOnly &&
          !disabled;

        const paddingRight = RightIcon
          ? "pr-13"
          : shouldShowClear
            ? "pr-8"
            : "pr-3";

        return (
          <FormItem>
            {label && (
              <FormLabel htmlFor={inputId} className={cn(classNameLabel)}>
                {label}
              </FormLabel>
            )}
            <FormControl>
              <div className="relative">
                <Input
                  id={inputId}
                  type={type}
                  placeholder={placeholder}
                  className={`h-13 p-3 rounded border-1 border-gray-500 ${paddingRight} ${className} [&_input::placeholder]:font-medium`}
                  readOnly={readOnly}
                  disabled={disabled}
                  {...field}
                />

                {shouldShowClear && (
                  <button
                    type="button"
                    onClick={() => field.onChange("")}
                    className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground ${
                      RightIcon ? "right-8" : "right-3"
                    }`}
                    tabIndex={-1}
                  >
                    <XIcon size={18} />
                  </button>
                )}

                {RightIcon && (
                  <button
                    type="button"
                    onClick={onIconClick}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    tabIndex={-1}
                  >
                    <RightIcon size={18} />
                  </button>
                )}
              </div>
            </FormControl>
            {!unshowMessage && <FormMessage keyT={keyT} />}
          </FormItem>
        );
      }}
    />
  );
}
